import { Form as BootstrapForm, Row, Col } from "react-bootstrap";
import RequiredReactSelect from "@components/FormsV2/RequiredReactSelect/RequiredReactSelect";
import Select from "react-select";
import { useSelector } from "react-redux";
import { RootState } from "@reducers/rootReducer";
import {
  useListOpenRouterKeysForAccountQuery,
  useListPromptContentsForAccountQuery,
} from "@generated";
import { PromptOverrideFormState, PROMPT_TYPE_OPTIONS } from "../types";
import { LLM_OPTIONS } from "../../../OpenRouterKeys/common/types/llmOptions";

interface OptionType {
  value: string | number;
  label: string;
}

interface PromptOverrideFormProps {
  formId?: string;
  formState: PromptOverrideFormState;
  setFormState: (state: PromptOverrideFormState) => void;
  editing: boolean;
  onSubmit?: () => void;
}

const PromptOverrideForm = ({
  formId,
  formState,
  setFormState,
  editing,
  onSubmit,
}: PromptOverrideFormProps): JSX.Element => {
  const { accountSlug } = useSelector((state: RootState) => state.slug);
  const { data: openRouterKeysData } = useListOpenRouterKeysForAccountQuery({
    slug: accountSlug,
  });
  const { data: promptContentsData } = useListPromptContentsForAccountQuery({
    slug: accountSlug,
  });

  const openRouterKeyOptions: OptionType[] = (openRouterKeysData?.data || [])
    .filter((key) => key.id)
    .map((key) => ({
      value: String(key.id),
      label: key.attributes?.name || `Key ${key.id}`,
    }));

  const selectedPromptType = PROMPT_TYPE_OPTIONS.find(
    (opt) => opt.value === formState.promptType
  );

  const selectedOpenRouterKey = openRouterKeyOptions.find(
    (opt) => String(opt.value) === formState.openRouterKeyId
  );

  const handlePromptTypeChange = (e: unknown) => {
    const option = e as OptionType | null;
    if (!option) return;
    const promptType = option.value as string;

    let content = formState.content;
    if (!editing && promptContentsData?.data) {
      const match = promptContentsData.data.find(
        (pc) => pc.attributes?.["prompt-type"] === promptType
      );
      if (match?.attributes?.content) {
        content = match.attributes.content;
      }
    }

    setFormState({ ...formState, promptType, content });
  };

  return (
    <BootstrapForm
      id={formId}
      className="promptOverrideForm"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit?.();
      }}
    >
      <Row>
        <Col md={6}>
          <RequiredReactSelect
            label="Prompt Type"
            options={PROMPT_TYPE_OPTIONS}
            value={selectedPromptType || undefined}
            onChange={handlePromptTypeChange}
            placeholder="Select a prompt type..."
            required
            isDisabled={editing}
          />
        </Col>
        {openRouterKeyOptions.length > 0 && (
          <Col md={6}>
            <BootstrapForm.Group className="formGroup">
              <BootstrapForm.Label>
                Which Open Router Keys should be used?
              </BootstrapForm.Label>
              <Select
                options={openRouterKeyOptions}
                value={selectedOpenRouterKey || null}
                onChange={(selected) => {
                  const option = selected as OptionType | null;
                  setFormState({
                    ...formState,
                    openRouterKeyId: option ? String(option.value) : "",
                  });
                }}
                isClearable
                placeholder="Select an Open Router Key..."
              />
            </BootstrapForm.Group>
          </Col>
        )}
      </Row>
      <Row>
        <Col>
          <BootstrapForm.Group className="formGroup">
            <BootstrapForm.Label>
              Which language models should this prompt be used with?
            </BootstrapForm.Label>
            <Select
              isMulti
              options={LLM_OPTIONS}
              value={formState.model.map((m) => ({
                value: m,
                label: m,
              }))}
              onChange={(selected) => {
                setFormState({
                  ...formState,
                  model: selected ? selected.map((s) => s.value) : [],
                });
              }}
              placeholder="Search and select LLMs..."
            />
            <small className="small">
              If you require specific LLMs, select them above. Otherwise the
              default models will be used.
            </small>
          </BootstrapForm.Group>
        </Col>
      </Row>
      <Row>
        <Col>
          <BootstrapForm.Group className="formGroup">
            <BootstrapForm.Label>Content</BootstrapForm.Label>
            <BootstrapForm.Control
              as="textarea"
              rows={20}
              value={formState.content}
              onChange={(e) =>
                setFormState({ ...formState, content: e.target.value })
              }
              required
            />
          </BootstrapForm.Group>
        </Col>
      </Row>
    </BootstrapForm>
  );
};

export default PromptOverrideForm;
